import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import JunoPage from "../../components/JunoPage/JunoPage";
import { HealthcareService, Location, Organization } from "fhir/r5";
import { NarrativeDisplay } from "@fyrstain/fhir-front-library";
import { Title } from "@fyrstain/hl7-front-library";
import { Badge, Button, Card } from "react-bootstrap";
import i18n from "i18next";
import HealthcareServiceService from "../../services/HealthcareServiceService";
import LocationService from "../../services/LocationService";
import OrganizationService from "../../services/OrganizationService";
import {
  getOfficialIdentifierValue,
  getReferenceValue,
  referenceMatchesValue,
} from "../../utils/fhirReferences";
import {
  getChildLocations,
  getLocationDisplayName,
  getLocationKey,
  getLocationPath,
} from "../../utils/locationTree";
import { resolveResourceIdByReference } from "../../utils/resourceLookup";
import { getOrganizationKey } from "../../utils/organizationTree";
import { useErrorNavigation } from "../../services/useErrorNavigation";
import "./LocationDetails.css";

const LocationDetails: FunctionComponent = () => {
  //////////////////////////////
  //        Navigation        //
  //////////////////////////////

  const navigate = useNavigate();

  //////////////////////////////
  //      Data loading        //
  //////////////////////////////

  const [loading, setLoading] = useState(false);
  const [fitFooter, setFitFooter] = useState(false);
  const { locationId } = useParams();
  const [location, setLocation] = useState<Location | null>(null);
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
  const [allHealthcareServices, setAllHealthcareServices] = useState<
    HealthcareService[]
  >([]);

  const onError = useErrorNavigation();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [locationResponse, locationsResponse, organizationsResponse, servicesResponse] =
        await Promise.all([
          LocationService.loadLocation(locationId as string),
          LocationService.loadAllLocations(),
          OrganizationService.loadAllOrganizations(),
          HealthcareServiceService.loadAllHealthcareServices(),
        ]);

      setFitFooter(!locationResponse?.text?.div);
      setLocation(locationResponse);
      setAllLocations(locationsResponse);
      setAllOrganizations(organizationsResponse);
      setAllHealthcareServices(servicesResponse);
    } catch (error) {
      onError(error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [locationId, onError]);

  useEffect(() => {
    load();
  }, [load]);

  //////////////////////////////
  //     Derived data         //
  //////////////////////////////

  const primaryIdentifier =
    getOfficialIdentifierValue(location?.identifier) || location?.id || "N/A";

  const locationKey =
    getOfficialIdentifierValue(location?.identifier) ||
    location?.id ||
    location?.name ||
    "";

  const locationPath = useMemo(
    () => getLocationPath(location, allLocations),
    [allLocations, location]
  );

  const childLocations = useMemo(
    () => getChildLocations(allLocations, location),
    [allLocations, location]
  );

  const parentLocation = allLocations.find((candidate) => {
    const candidateKey = getLocationKey(candidate);
    const parentReferenceValue = getReferenceValue(location?.partOf);
    return (
      candidateKey === parentReferenceValue || candidate.id === parentReferenceValue
    );
  });

  const managingOrganization = allOrganizations.find((candidate) => {
    const managingReferenceValue = getReferenceValue(location?.managingOrganization);
    const managingReferenceDisplay = location?.managingOrganization?.display?.trim() ?? "";
    const candidateIdentifiers = candidate.identifier?.map((identifier) => identifier.value ?? "") ?? [];
    const candidateValues = [
      candidate.id ?? "",
      candidate.name ?? "",
      getOrganizationKey(candidate),
      ...candidateIdentifiers,
    ]
      .map((value) => value.trim())
      .filter(Boolean);

    return (
      candidateValues.includes(managingReferenceValue) ||
      candidateValues.includes(managingReferenceDisplay)
    );
  });

  const managingOrganizationTargetId =
    managingOrganization?.id ||
    resolveResourceIdByReference(
      allOrganizations,
      location?.managingOrganization
    );

  const exercisedHealthcareServices = useMemo(() => {
    if (!locationKey) {
      return [];
    }

    return allHealthcareServices.filter((service) =>
      service.location?.some((reference) =>
        referenceMatchesValue(reference, locationKey)
      )
    );
  }, [allHealthcareServices, locationKey]);

  //////////////////////////////
  //          Actions         //
  //////////////////////////////

  const goToLocationDetails = useCallback(
    (targetLocationId?: string | null) => {
      if (targetLocationId) {
        navigate(`/Location/${encodeURIComponent(targetLocationId)}`);
      }
    },
    [navigate]
  );

  const goToOrganizationDetails = useCallback(
    (organizationId?: string | null) => {
      if (organizationId) {
        navigate(`/Organization/${encodeURIComponent(organizationId)}`);
      }
    },
    [navigate]
  );

  const goToHealthcareServiceDetails = useCallback(
    (healthcareServiceId?: string | null) => {
      if (healthcareServiceId) {
        navigate(`/HealthcareService/${encodeURIComponent(healthcareServiceId)}`);
      }
    },
    [navigate]
  );

  const locationTypeLabels = (location?.type ?? [])
    .flatMap((entry) => entry.coding ?? [])
    .map((coding) => coding.display ?? coding.code ?? "")
    .filter(Boolean);

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <JunoPage
      titleKey="title.locationdetails"
      loading={loading}
      fitFooter={fitFooter}
      needsLogin={true}
    >
      <div className="section">
        <Card className="card">
          <Card.Header>
            <Title level={2} prefix={location?.resourceType} content={location?.id} />
          </Card.Header>
          <Card.Body>
            {location?.text?.div ? (
              <NarrativeDisplay
                resource={location}
                onError={onError}
                message={i18n.t("title.error.narrativenotfound")}
              />
            ) : (
              <div className="location-details-layout">
                <div className="location-details-badges">
                  {location?.status && (
                    <Badge bg={location.status === "active" ? "success" : "secondary"}>
                      {location.status}
                    </Badge>
                  )}
                  {location?.mode && <Badge bg="info">{location.mode}</Badge>}
                </div>
                <div className="location-details-grid">
                  <div className="location-detail-card location-detail-card-full">
                    <span className="location-detail-label">
                      {i18n.t("label.locationpath")}
                    </span>
                    <div className="location-detail-value location-path">
                      {locationPath.length > 0 ? (
                        locationPath.map((item, index) => {
                          const isLast = index === locationPath.length - 1;
                          const pathKey =
                            getLocationKey(item) || item.id || item.name || `path-${index}`;

                          return (
                            <span className="location-path-item" key={pathKey}>
                              <Button
                                variant={isLast ? "primary" : "outline-primary"}
                                size="sm"
                                className="rounded-pill"
                                onClick={() => goToLocationDetails(item.id ?? getLocationKey(item))}
                              >
                                {getLocationDisplayName(item)}
                              </Button>
                              {!isLast && <span className="location-path-separator">/</span>}
                            </span>
                          );
                        })
                      ) : (
                        <span>N/A</span>
                      )}
                    </div>
                  </div>
                  <div className="location-detail-card">
                    <span className="location-detail-label">
                      {i18n.t("label.locationname")}
                    </span>
                    <span className="location-detail-value">
                      {location?.name ?? "N/A"}
                    </span>
                  </div>
                  <div className="location-detail-card">
                    <span className="location-detail-label">
                      {i18n.t("label.identifier")}
                    </span>
                    <span className="location-detail-value">{primaryIdentifier}</span>
                  </div>
                  <div className="location-detail-card">
                    <span className="location-detail-label">
                      {i18n.t("label.locationstatus")}
                    </span>
                    <span className="location-detail-value">
                      {location?.status ?? "N/A"}
                    </span>
                  </div>
                  <div className="location-detail-card">
                    <span className="location-detail-label">{i18n.t("label.mode")}</span>
                    <span className="location-detail-value">
                      {location?.mode ?? "N/A"}
                    </span>
                  </div>
                  <div className="location-detail-card">
                    <span className="location-detail-label">{i18n.t("label.type")}</span>
                    <span className="location-detail-value">
                      {locationTypeLabels.length > 0
                        ? locationTypeLabels.join(", ")
                        : "N/A"}
                    </span>
                  </div>
                  <div className="location-detail-card">
                    <span className="location-detail-label">
                      {i18n.t("label.managingorganization")}
                    </span>
                    <span className="location-detail-value">
                      {managingOrganizationTargetId ? (
                        <Button
                          variant="primary"
                          size="sm"
                          className="rounded-pill"
                          onClick={() =>
                            goToOrganizationDetails(managingOrganizationTargetId)
                          }
                        >
                          {managingOrganization?.name ??
                            managingOrganization?.id ??
                            getReferenceValue(location?.managingOrganization)}
                        </Button>
                      ) : (
                        getReferenceValue(location?.managingOrganization) || "N/A"
                      )}
                    </span>
                  </div>
                  <div className="location-detail-card">
                    <span className="location-detail-label">
                      {i18n.t("label.parentlocation")}
                    </span>
                    <span className="location-detail-value">
                      {parentLocation ? (
                        <Button
                          variant="primary"
                          size="sm"
                          className="rounded-pill"
                          onClick={() => goToLocationDetails(parentLocation.id)}
                        >
                          {getLocationDisplayName(parentLocation)}
                        </Button>
                      ) : (
                        getReferenceValue(location?.partOf) || "N/A"
                      )}
                    </span>
                  </div>
                  <div className="location-detail-card location-detail-card-full">
                    <span className="location-detail-label">
                      {i18n.t("label.childlocations")}
                    </span>
                    <div className="location-detail-value location-related-list">
                      {childLocations.length > 0 ? (
                        childLocations.map((child) => (
                          <Button
                            key={child.id ?? getLocationKey(child)}
                            size="sm"
                            variant="outline-primary"
                            className="rounded-pill"
                            onClick={() => goToLocationDetails(child.id)}
                          >
                            {getLocationDisplayName(child)}
                          </Button>
                        ))
                      ) : (
                        <span>N/A</span>
                      )}
                    </div>
                  </div>
                  <div className="location-detail-card location-detail-card-full">
                    <span className="location-detail-label">
                      {i18n.t("label.healthcareservices")}
                    </span>
                    <div className="location-detail-value location-related-list">
                      {exercisedHealthcareServices.length > 0 ? (
                        exercisedHealthcareServices.map((service) => (
                          <Button
                            key={service.id ?? service.name}
                            size="sm"
                            variant="outline-primary"
                            className="rounded-pill"
                            onClick={() => goToHealthcareServiceDetails(service.id)}
                          >
                            {service.name ?? service.id ?? "N/A"}
                          </Button>
                        ))
                      ) : (
                        <span>N/A</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </JunoPage>
  );
};

export default LocationDetails;
